import { AdminServiceGridSortColumn } from "@/Administration/Models/Enums/AdminServiceGridSortColumn";
import { LogEventTypeEnum } from "@/Administration/Models/Enums/LogEventTypeEnum";
import { CostCenterUIModel } from "@/Administration/Models/UI/CostCenterUIModel";
import { BillableServiceEditMode, LanguageOptions, LogIntervalType, ServiceComboEditMode } from "@/Administration/Models/UI/Enums/Administration";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { DefaultCanadaTaxes } from "@/Common/Api/MedfarDictionaries/Dto/Responses/DefaultCanadaTaxes";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { DayOfWeek } from "@/Common/Models/DayOfWeek";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { Guid } from "@/Common/Models/Guid";
import { Locale } from "@/Common/Models/Localization/Locale";
import { LocalizedTextDto } from "@/Common/Models/LocalizedTextDto";
import { Person } from "@/Common/Models/Person/Person";
import { IComboOrServiceItem } from "@/Common/Models/Services/BaseServiceDefinitionModel";
import { ServiceComboItemType } from "@/Common/Models/Services/Enums/ServiceComboItemType";
import { ServiceComboDefinitionModel } from "@/Common/Models/Services/ServiceComboDefinitionModel";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { ServiceOrComboTableItem } from "@/Common/Models/Services/ServiceOrComboTableItem";
import { SiteSpecialtyId } from "@/Common/Models/Sites/SiteSpecialtyId";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeGroupUIModel } from "@/Common/Models/UI/Employee/EmployeeGroupUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { AdminTabViews, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { GenericCheckboxUIModel } from "@/Common/Models/UI/Inputs/GenericCheckboxUIModel";
import { LookupUIModel } from "@/Common/Models/UI/Inputs/LookupUIModel";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ContactDirectoryClient } from "@/ContactDirectory/Api/ContactDirectoryClient";
import { IJAdminTabModel } from "../Models/JSONInterfaces/IJAdminTabModel";
import { IJBillableServiceModel } from "../Models/JSONInterfaces/IJBillableServiceModel";
import { IJGroupTabModel } from "../Models/JSONInterfaces/IJGroupTabModel";
import { IJLogTabModel } from "../Models/JSONInterfaces/IJLogTabModel";
import { IJServiceComboGridModel } from "../Models/JSONInterfaces/IJServiceComboGridModel";
import { IJUsersSearchModel } from "../Models/JSONInterfaces/IJUsersSearchModel";
import { IJUsersTabModel } from "../Models/JSONInterfaces/IJUsersTabModel";
import { LogItem } from "../Models/Log/LogItem";
import { ServiceComboDisplaySettingsModel } from "../Models/Settings/ServiceComboDisplaySettingsModel";
import { AdminBillableServiceGridSortColumn } from "../Models/UI/BillableService/AdminBillableServiceGridSortColumn";
import { ImportClickableTextEnum } from "../Models/UI/ClickableTexts/ImportClickableTextEnum";
import { GroupEditMode } from "../Models/UI/Group/GroupEditMode";
import { UserEditMode } from "../Models/UI/Users/UserEditMode";
import { UsersSubmenuEnum } from "../Models/UI/Users/UsersSubmenuEnum";

interface LocalizedTextSite {
    languageName: string;
    locale: Locale,
    value: string,
    propertyName: string,
}

class AdministrationHelper {
    public static maxNoteLengthLimit: number = 1000;

    public static orderedLocales = [Locale.en, Locale.fr, Locale.es];

    public static getAvailableLanguages(): KeyValuePair<string, string>[] {
        return settingsStore.configuration
            .get("LanguageOptions")
            .toArray()
            .map(lang => lang.Value === "es-MX"
                ? new KeyValuePair("Español", "es")
                : lang
            );
    }

    public static BuildServiceComboSearchGridModel(userDisplaySettings: ServiceComboDisplaySettingsModel): IJServiceComboGridModel {
        let gridModel: IJServiceComboGridModel = this.GetDefaultServiceComboSearchGridModel();

        gridModel.Sites
            .map((sites: SiteUIModel) => {
                if (userDisplaySettings.SitesId.indexOf(sites.Id) >= 0)
                    sites.Checked = true;
                else {
                    sites.Checked = false;
                    gridModel.Sites_AllSelected = false;
                }
            });

        gridModel.Specializations
            .map((specialities: LookupUIModel) => {
                if (userDisplaySettings.SpecializationsId.indexOf(specialities.Id) >= 0)
                    specialities.Checked = true;
                else {
                    specialities.Checked = false;
                    gridModel.Specializations_AllSelected = false;
                }
            });

        gridModel.Types
            .map((type: GenericCheckboxUIModel<ServiceComboItemType>) => {
                if (userDisplaySettings.TypesId.indexOf(type.Id) >= 0)
                    type.Checked = true;
                else {
                    type.Checked = false;
                    gridModel.Types_AllSelected = false;
                }
            });

        gridModel.EmployeesGlobalCache
            .map((employee: EmployeeUIModel) => {
                if (userDisplaySettings.EmployeeIds.indexOf(employee.Id) >= 0)
                    employee.Checked = true;
                else {
                    employee.Checked = false;
                    gridModel.Employees_AllSelected = false;
                }
            });

        gridModel.Status_AllSelected = userDisplaySettings.Status_AllSelected;
        gridModel.Status_IsActiveChecked = userDisplaySettings.Status_IsActiveChecked;
        gridModel.Status_IsInactiveChecked = userDisplaySettings.Status_IsInactiveChecked;

        return gridModel;
    }

    public static GetDefaultServiceComboSearchGridModel(): IJServiceComboGridModel {
        // get sites
        let sites: SiteUIModel[] = [];
        sites = cacheStore.GetSites(CheckboxTarget.Myle_Admin_ServiceCombo_Sites_Check);

        // get specialities
        let specializationLookupsUIModels: LookupUIModel[] = this.GetSpecializationsBasedOnSites(sites);

        // get service/combo type
        let types: GenericCheckboxUIModel<ServiceComboItemType>[] = [];
        let serviceUIModel = new GenericCheckboxUIModel<ServiceComboItemType>();
        serviceUIModel.Id = ServiceComboItemType.Service.toString();
        serviceUIModel.Checked = true;
        serviceUIModel.Name = cacheStore.GetString("ServiceLabel");
        serviceUIModel.Visible = true;
        serviceUIModel.Target = CheckboxTarget.Myle_Admin_ServiceCombo_Type_Check;
        serviceUIModel.Item = ServiceComboItemType.Service;
        types.push(serviceUIModel);
        let comboUiModel = new GenericCheckboxUIModel<ServiceComboItemType>();
        comboUiModel.Id = ServiceComboItemType.Combo.toString();
        comboUiModel.Checked = true;
        comboUiModel.Name = cacheStore.GetString("ComboLabel");
        comboUiModel.Visible = true;
        comboUiModel.Target = CheckboxTarget.Myle_Admin_ServiceCombo_Type_Check;
        comboUiModel.Item = ServiceComboItemType.Combo;
        types.push(comboUiModel);

        // get employees
        let employeesCache = this.GetEmployeeCache();
        employeesCache = this.GetEmployeesBasedOnSpecialization(employeesCache, specializationLookupsUIModels);
        employeesCache.forEach(x => x.Checked = true);

        // filter services and combos
        let filteredServices: ServiceOrComboTableItem<IComboOrServiceItem>[] = [];

        // this.FilterAndOrderServiceComboDefinitions(cacheStore.ServiceDefinitionCache.toArray(), cacheStore.ServiceDefinitionComboCache.toArray(), employeesCache, sites, types, specializationLookupsUIModels, AdminServiceGridSortColumn.Name, SortDirection.DESC, true, false);
        let serviceComboModel: IJServiceComboGridModel =
        {
            ExportServiceOpen: false,
            SearchSettingsOpen: false,
            Sites: sites,
            Sites_AllSelected: true,
            Specializations: specializationLookupsUIModels,
            Specializations_AllSelected: true,
            Types: types,
            Types_AllSelected: true,
            EmployeesGlobalCache: employeesCache,
            EmployeesFiltered: employeesCache,
            Employees_AllSelected: true,
            EmployeesSearchText: "",
            FilteredCombosAndServices: filteredServices,
            GridSortDirection: SortDirection.ASC,
            GridSortColumn: AdminServiceGridSortColumn.Name,
            CurrentEditMode: ServiceComboEditMode.View,
            CurrentEditedType: ServiceComboItemType.Combo,
            EditServiceComponentModel: null,
            DisplayEditServiceComponent: false,
            DisplayPortalDetailsComponent: false,
            Status_AllSelected: false,
            Status_IsActiveChecked: true,
            Status_IsInactiveChecked: false,
            SaveDisplaySettings: false,
            NotificationConfiguration: {
                show: false,
                serviceOrCombo: null,
                notificationConfiguration: null,
                defaultNotificationConfiguration: null
            }
        };

        return serviceComboModel;
    }

    public static GetDefaultForAdminTab(): IJAdminTabModel {
        if (!cacheStore.ClinicSettings.EnableUserSettingsUiImprovements) {
            return {
                CurrentView: AdminTabViews.ServiceCombos
            };
        }

        const adminPermission: Record<AdminTabViews, EnumMyleLoginPermission> = {
            [AdminTabViews.ServiceCombos]: EnumMyleLoginPermission.Administration_ServiceCombosTabPermission,
            [AdminTabViews.Sites]: EnumMyleLoginPermission.Administration_SiteTabPermission,
            [AdminTabViews.BillableServices]: EnumMyleLoginPermission.Administration_BillableServicesTabPermission,
            [AdminTabViews.Groups]: EnumMyleLoginPermission.Administration_CommunicationGroupsTabPermission,
            [AdminTabViews.Users]: EnumMyleLoginPermission.Administration_UsersTabPermission,
            [AdminTabViews.Log]: EnumMyleLoginPermission.Administration_LogsTabPermission,
            [AdminTabViews.Questionnaires]: EnumMyleLoginPermission.Administration_QuestionnairesTabPermission,
            [AdminTabViews.ExternalConsultationHistory]: EnumMyleLoginPermission.Administration_ExternalConsultationTabPermission,
            [AdminTabViews.Roles]: EnumMyleLoginPermission.Administration_RolesTabPermission
        };

        const defaultView = Object.keys(adminPermission).find(key => {
            const index = Number(key);
            return adminPermission[index] &&
                sessionStore.hasPermission(adminPermission[index]);
        });

        return {
            CurrentView: Number(defaultView) as AdminTabViews
        };
    }

    public static GetDefaultUsersTab(): IJUsersTabModel {
        let userSearchModel = Immutable.fromJS(AdministrationHelper.GetDefaultUsersSearch());
        let defaultServiceComboModel = this.GetDefaultServiceComboSearchGridModel();
        let filteredServices: ServiceOrComboTableItem<IComboOrServiceItem>[] = AdministrationHelper.FilterAndOrderServiceComboDefinitions(cacheStore.ServiceDefinitionCache.toArray(), [], defaultServiceComboModel.EmployeesFiltered, defaultServiceComboModel.Sites, defaultServiceComboModel.Types, defaultServiceComboModel.Specializations, AdminServiceGridSortColumn.Name, SortDirection.DESC, true, false, "");

        defaultServiceComboModel.FilteredCombosAndServices = filteredServices;

        let jsn = {
            SearchMenuOpen: true,
            CreatePopupOpen: false,
            UsersSearchModel: userSearchModel,
            SelectedEmployee: null,
            SelectedSubmenuItem: UsersSubmenuEnum.Demography,
            FilteredCombosAndServices: null,
            PdfTagsCache: null,
            EditedUser: null,
            UserEditMode: UserEditMode.View,
            UserType: null,
            CliniciansAsKVPList: [],
            ClinicianIdForOrphanLabs: "",
            ClinicianDateFromOrphanLabs: null,
            ClinicianDateToOrphanLabs: null,
            NewUsefulLink: "",
            IsOpenAddNewServiceCombo: false,
            SearchSettingsOpen: false,
            IsModifyPhoneMode: false,
            IsModifyEmailMode: false,
            IsModifyUserNameMode: false,
            IsModifyUserTypeMode: false,
            IsModifyPasswordMode: false,
            showClickableTextFromAnotherUser: false,
            ClickableTextSpecificUser: null,
            IsCreateClickableTextOpen: false,
            UserServiceComboGridModel: Immutable.fromJS(defaultServiceComboModel),
            IsRefreshClickableText: false,
            IsImportClickableTextOpen: false,
            AllTabNamesClickableTextForSpecificContext: [],
            AllListOfClickableTextDetails: [],
            SelectedTabImportClickableText: ImportClickableTextEnum.Tab,
            AllUsersWithClickableText: [],
            SpecificUserForImportClickableText: null,
            AllListOfClickableTextDetailsForSpecificUser: [],
            LanguageOptionClickableText: this.getLanguageStringToEnum(),
            ResendEmailVerificationCodeInProgress: false
        };

        jsn.CliniciansAsKVPList = this.getCliniciansAsKeyValuePairs();

        return jsn;
    }

    public static GetDefaultGroupTab(): IJGroupTabModel {
        let searchGroupResult: EmployeeGroupUIModel[] = [];
        let defaultServiceComboModel = this.GetDefaultServiceComboSearchGridModel();
        let filteredServices: ServiceOrComboTableItem<IComboOrServiceItem>[] = AdministrationHelper.FilterAndOrderServiceComboDefinitions(cacheStore.ServiceDefinitionCache.toArray(), [], defaultServiceComboModel.EmployeesFiltered, defaultServiceComboModel.Sites, defaultServiceComboModel.Types, defaultServiceComboModel.Specializations, AdminServiceGridSortColumn.Name, SortDirection.DESC, true, false, "");

        defaultServiceComboModel.FilteredCombosAndServices = filteredServices;

        let groupTabModel: IJGroupTabModel = {
            SearchMenuOpen: true,
            SearchGroupResult: searchGroupResult,
            IsCreateNewGroupPopupOpen: false,
            SelectedGroup: null,
            GroupName: "",
            EditedGroup: null,
            GroupEditMode: GroupEditMode.View,
            PDFSearchKeywords: [],
            SelectePDFTagIds: [],
            NewUsefulLink: "",
            IsOpenAddNewServiceCombo: false,
            UserServiceComboGridModel: Immutable.fromJS(defaultServiceComboModel),
            FilteredCombosAndServices: [],
            SearchText: "",
            IsImportClickableTextOpen: false,
            SelectedTabImportClickableText: ImportClickableTextEnum.Tab,
            GroupClickableTextItems: [],
            SpecificGroupForImportClickableText: null,
            AllListOfClickableTextDetailsForSpecificGroup: [],
            ContextForImportClickableText: "",
            AllListOfClickableTextDetails: [],
            AllTabNamesClickableTextForSpecificContext: [],
            AllGroupsWithClickableText: [],
            IsRefreshClickableText: false,
            IsRenameGroupPopupOpen: false,
            LanguageOptionClickableText: this.getLanguageStringToEnum()

        };

        return groupTabModel;
    }

    private static getLanguageStringToEnum(): LanguageOptions {
        const language = sessionStore.locale;
        switch (language) {
            case "en":
                return LanguageOptions.English;
            case "es":
                return LanguageOptions.Spanish;
            default:
                return LanguageOptions.French;
        }
    }

    public static GetDefaultUsersSearch(): IJUsersSearchModel {
        let searchEmployeeResult: Person[] = [];

        let userSearchModel: IJUsersSearchModel = {
            HideInactiveUsers: true,
            SearchEmployeeResult: searchEmployeeResult,
            SelectedEmployeeId: "",
            SearchText: "",
            IsActiveUser: false,
            ReinitPopupOpen: false

        };

        return userSearchModel;
    }

    public static GetDefaultBillableServiceTab(): IJBillableServiceModel {
        // get sites
        let sites: SiteUIModel[] = [];
        sites = cacheStore.GetSites(CheckboxTarget.Myle_Admin_BillableService_Sites_Check);

        // get cost centers
        let costCenters: CostCenterUIModel[] = [];
        costCenters = cacheStore.GetCostCenters(CheckboxTarget.Myle_Admin_BillableService_CostCenters_Check);

        let billableServiceModel: IJBillableServiceModel = {
            SearchMenuOpen: false,
            GridSortDirection: SortDirection.ASC,
            BillableServiceSearchText: "",
            FilteredBillableServices: [],
            GridSortColumn: AdminBillableServiceGridSortColumn.Number,
            AllBillableServices: [],
            Sites: sites,
            Sites_AllSelected: true,
            CostCenters: costCenters,
            CostCenters_AllSelected: true,
            EditedBillableService: null,
            CurrentEditMode: BillableServiceEditMode.Edit,
            DisplayEditBillableServiceComponent: false,

            SelectedPageSizeOption: 50,
            PageSizeOptions: [50, 100, 150],
            SelectedPageNumber: 1,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            CurrentPage: 0,
            ColumnOrderingPopupOpen: false,
            ShowNullSites: true,
            ShowNullCostCenters: true
        };

        return billableServiceModel;
    }

    public static GetSpecializations(services: ServiceDefinitionModel[]): LookupUIModel[] {
        let specializationLookupsUIModels: LookupUIModel[] = [];
        let specializationLookups: { [lookupId: string]: FlatLookupView } = {};
        services.forEach(x => {
            if (x.Model != null) {
                if (Object.keys(specializationLookups).indexOf(x.Model.SpecializationLookupId) == -1) {
                    specializationLookups[x.Model.SpecializationLookupId] = cacheStore.GetLookupById(x.Model.SpecializationLookupId);
                }
            }
        });
        Object.keys(specializationLookups).forEach(key => {
            let flatlookup: FlatLookupView = specializationLookups[key];

            let newui = new LookupUIModel();
            newui.Checked = true;
            newui.Id = flatlookup.LookupID;
            newui.Visible = true;
            newui.Name = cacheStore.GetLocalizedValueByLookupId(flatlookup.LookupID);
            newui.Target = CheckboxTarget.Myle_Admin_ServiceCombo_Speciality_Check;
            newui.Code = flatlookup.LookupCode;
            specializationLookupsUIModels.push(newui);
        });

        return specializationLookupsUIModels;
    }

    public static GetSpecializationsBasedOnSites(sites: SiteUIModel[]): LookupUIModel[] {
        if (sites == null)
            return [];

        let siteIds: string[] = [];
        sites.forEach(x => {
            if (x.Checked) {
                siteIds.push(x.Id);
            }
        });

        let services: ServiceDefinitionModel[] = [];
        services = cacheStore.ServiceDefinitionCache
            .toArray()
            .filter(x => siteIds.some(id => id == x.Model.SiteId));
        let specializationLookupsUIModels: LookupUIModel[] = AdministrationHelper.GetSpecializations(services);

        return specializationLookupsUIModels;
    }

    public static GetEmployeeCache(): EmployeeUIModel[] {
        return cacheStore.GetCliniciansAsEmployees(CheckboxTarget.Myle_Admin_ServiceCombo_Employees_Check, false);
    }

    public static GetEmployeeCacheForAdminLog(): EmployeeUIModel[] {
        return cacheStore.GetCliniciansAsEmployees(CheckboxTarget.Myle_Admin_Log_Users_Check, true);
    }

    public static GetEmployeesBasedOnSpecialization(employees: EmployeeUIModel[], specializations: LookupUIModel[]): EmployeeUIModel[] {
        if (employees == null || specializations == null)
            return [];

        let specializationLookupIds: string[] = [];
        specializations.forEach(x => {
            if (x.Checked) {
                specializationLookupIds.push(x.Id);
            }
        });

        let resultEmployees: EmployeeUIModel[] = [];
        employees.forEach(x => {
            if (x.Model != null) {
                if (x.Model.ServicesOffered?.any()) {
                    var hasSpeciality = x.Model.ServicesOffered.some(s => {
                        if (s.ServiceDefinitionModel != null && s.ServiceDefinitionModel.Model != null) {
                            return specializationLookupIds.indexOf(s.ServiceDefinitionModel.Model.SpecializationLookupId) > -1;
                        }
                        return false;
                    });

                    if (hasSpeciality || !specializationLookupIds.any())
                        resultEmployees.push(x);
                }
            }
        });

        return resultEmployees;
    }

    public static FilterAndOrderServiceComboDefinitions(serviceDefinitions: ServiceDefinitionModel[], serviceComboDefinitions: ServiceComboDefinitionModel[], employees: EmployeeUIModel[], sites: SiteUIModel[], types: GenericCheckboxUIModel<ServiceComboItemType>[], specializations: LookupUIModel[], sortColumn: AdminServiceGridSortColumn, sortDirection: SortDirection, isActiveSelected: boolean = true, isInactiveSelected: boolean = false, nameFilterString: string = null): ServiceOrComboTableItem<IComboOrServiceItem>[] {
        let result: ServiceOrComboTableItem<IComboOrServiceItem>[] = [];

        // apply filter name string if necessary
        if (nameFilterString != null && nameFilterString.length > 0) {
            serviceDefinitions = serviceDefinitions
                .filterSearch(nameFilterString, item => item.Name);

            serviceComboDefinitions = serviceComboDefinitions
                .filterSearch(nameFilterString, item => item.Name);
        }

        // exit if everything is null
        if (serviceDefinitions == null || serviceComboDefinitions == null || employees == null || sites == null || specializations == null || types == null)
            return result;

        let selectedTypes = types
            .filter((x: GenericCheckboxUIModel<ServiceComboItemType>) => x.Checked);

        // aggregate service and combos together
        if (selectedTypes.some(x => x.Item == ServiceComboItemType.Service) || !selectedTypes.any()) {
            serviceDefinitions.forEach(x => {
                let serviceOrCombo: ServiceOrComboTableItem<IComboOrServiceItem> = new ServiceOrComboTableItem<ServiceDefinitionModel>(x);
                result.push(serviceOrCombo);
            });
        }
        if (selectedTypes.some(x => x.Item == ServiceComboItemType.Combo) || !selectedTypes.any()) {
            serviceComboDefinitions.forEach(x => {
                let serviceOrCombo: ServiceOrComboTableItem<IComboOrServiceItem> = new ServiceOrComboTableItem<ServiceComboDefinitionModel>(x);
                result.push(serviceOrCombo);
            });
        }

        // get filters
        let selectedEmployees = employees.filter((x: EmployeeUIModel) => x.Checked);
        let selectedEmployeeContactFacetIdsToFilter = selectedEmployees.map(x => x.ContactFacetId);
        let selectedSites = sites.filter((x: SiteUIModel) => x.Checked);
        let selectedSiteIds = selectedSites.map(x => x.Id);

        let selectedSpecializations = specializations.filter((x: LookupUIModel) => x.Checked);
        let selectedSpecializationIds = selectedSpecializations.map(x => x.Id);

        // filter
        result = result.filter((serviceOrCombo: ServiceOrComboTableItem<IComboOrServiceItem>) => {
            let isValid = true;

            if (serviceOrCombo.Type == ServiceComboItemType.Service) {
                let serviceModel: ServiceDefinitionModel = serviceOrCombo.Model as ServiceDefinitionModel;

                // check if service site is selected
                if (selectedSiteIds.any())
                    isValid = isValid && selectedSiteIds.indexOf(serviceModel.Model.SiteId) > -1;

                // check if service specialization is selected
                if (selectedSpecializationIds.any())
                    isValid = isValid && selectedSpecializationIds.indexOf(serviceModel.Model.SpecializationLookupId) > -1;

                // check if at least one of the employee selected has this service
                if (isActiveSelected && selectedEmployeeContactFacetIdsToFilter.any()) {
                    isValid =
                        isValid
                        && serviceModel.Employees.some(
                            employee => selectedEmployeeContactFacetIdsToFilter.indexOf(employee.ContactFacetId) > -1
                        );
                }
            }
            else if (serviceOrCombo.Type == ServiceComboItemType.Combo) {
                let comboModel: ServiceComboDefinitionModel = serviceOrCombo.Model as ServiceComboDefinitionModel;
                if (selectedTypes.any())
                    isValid = selectedTypes.some(x => x.Item == ServiceComboItemType.Combo);

                // check if combo site is selected
                if (selectedSiteIds.any())
                    isValid = isValid && selectedSiteIds.indexOf(comboModel.Model.SiteId) > -1;

                // check if combo main specialization is selected
                if (selectedSpecializationIds.any())
                    isValid = isValid && selectedSpecializationIds.indexOf(comboModel.Model.SpecializationLookupId) > -1;

                // check if at least one of the employee selected has this combo
                if (isActiveSelected && selectedEmployeeContactFacetIdsToFilter.any()) {
                    isValid =
                        isValid
                        && comboModel.ServiceDefinitions.some(
                            serviceDef => serviceDef.Employees.some(
                                employee => selectedEmployeeContactFacetIdsToFilter.indexOf(employee.ContactFacetId) > -1
                            )
                        );
                }
            }
            else
                isValid = false;

            if (isInactiveSelected) {
                // if both inactive and active are selected, it is valid
                if (isActiveSelected)
                    isValid = isValid || serviceOrCombo.NbEmployees === 0;
                else
                    // if only inactivated selected, ensure that the service or combo has no employee
                    isValid = isValid && serviceOrCombo.NbEmployees === 0;
            }
            else {
                // if only active is selected, ensure at least one employee is in the service or combo
                if (isActiveSelected)
                    isValid = isValid && serviceOrCombo.NbEmployees > 0;
            }

            return isValid;
        });

        // apply ordering
        let isDescending: boolean = sortDirection == SortDirection.DESC;
        let fieldName = "";
        switch (sortColumn) {
            case AdminServiceGridSortColumn.Details:
                fieldName = "NbEmployees";
                break;
            case AdminServiceGridSortColumn.Duration:
                fieldName = "Duration";
                break;
            case AdminServiceGridSortColumn.Name:
                fieldName = "Name";
                break;
            case AdminServiceGridSortColumn.Price:
                fieldName = "Price";
                break;
            case AdminServiceGridSortColumn.Site:
                fieldName = "Site";
                break;
            case AdminServiceGridSortColumn.Speciality:
                fieldName = "Speciality";
                break;
            case AdminServiceGridSortColumn.Type:
                fieldName = "Type";
                break;
            default:
                break;
        }

        result = ArrayHelper.SortDynamic(result, fieldName, isDescending);

        return result;
    }

    public static GetDefaultLogTabModel(): IJLogTabModel {
        let logTabModel: IJLogTabModel = {
            LogSearchText: "",
            TableData: Immutable.List([]),
            DateFrom: null,
            DateTo: null,
            SelectAllUsersChecked: false,
            UserSearchText: "",
            UsersFiltered: Immutable.List([]),
            ActionSearchText: "",
            ActionTypeFiltered: Immutable.List([]),
            NbAccess: null,
            Interval: null,
            IntervalType: LogIntervalType.Minute,

            SelectedPageSizeOption: 50,
            PageSizeOptions: Immutable.List([50, 100, 150]),
            SelectedPageNumber: 1,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            CurrentPage: 0,
            ColumnOrderingPopupOpen: false
        };

        return logTabModel;
    }

    public static convertEventTypeEnumToString(eventType: LogEventTypeEnum): string {
        let str: string = "";

        switch (eventType) {
            case LogEventTypeEnum.Login:
                str = cacheStore.GetString("LoginLabel");
                break;
            case LogEventTypeEnum.ChangePassword:
                str = cacheStore.GetString("ChangePasswordLabel");
                break;
            case LogEventTypeEnum.MedicalNoteCreate:
                str = cacheStore.GetString("CreateMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteApprove:
                str = cacheStore.GetString("ApproveMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteSave:
                str = cacheStore.GetString("SaveMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteAddItem:
                str = cacheStore.GetString("AddItemMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteItemDeletion:
                str = cacheStore.GetString("MedicalItemSummaryDeletionLabel");
                break;
            case LogEventTypeEnum.MedicalNoteAddConsultationRequest:
                str = cacheStore.GetString("AddConsultationRequestionMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteRemoveConsultationRequest:
                str = cacheStore.GetString("MedicalNoteRemoveConsultationRequestLabel");
                break;
            case LogEventTypeEnum.MedicalNoteAddLabRequest:
                str = cacheStore.GetString("AddLabRequestionMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteRemoveLabRequest:
                str = cacheStore.GetString("MedicalNoteRemoveLabRequestLabel");
                break;
            case LogEventTypeEnum.MedicalNoteSendForApproval:
                str = cacheStore.GetString("SendForApprovalMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteChangeConsultationDetails:
                str = cacheStore.GetString("ChangeConsultationDetailsMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteDelete:
                str = cacheStore.GetString("DeleteMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalNoteUnapprove:
                str = cacheStore.GetString("UnapproveMedicalNoteLabel");
                break;
            case LogEventTypeEnum.MedicalDocumentCreate:
                str = cacheStore.GetString("CreateMedicalDocumentLabel");
                break;
            case LogEventTypeEnum.MedicalDocumentApprove:
                str = cacheStore.GetString("ApproveMedicalDocumentLabel");
                break;
            case LogEventTypeEnum.MedicalDocumentUnapprove:
                str = cacheStore.GetString("UnapproveMedicalDocumentLabel");
                break;
            case LogEventTypeEnum.PatientFileOpen:
                str = cacheStore.GetString("OpenAPatientFileLabel");
                break;
            case LogEventTypeEnum.PrescriptionPrint:
                str = cacheStore.GetString("PrintAPrescriptionLabel");
                break;
            case LogEventTypeEnum.PrescriptionAdd:
                str = cacheStore.GetString("AddPrescriptionLabel");
                break;
            case LogEventTypeEnum.PrescriptionDeletePending:
                str = cacheStore.GetString("DeletePendingPrescriptionLabel");
                break;
            case LogEventTypeEnum.ActiveMedicationModification:
                str = cacheStore.GetString("ModifyMedicationLabel");
                break;
            case LogEventTypeEnum.MedicationStatusModification:
                str = cacheStore.GetString("MedicationStatusChangeLabel");
                break;
            case LogEventTypeEnum.RecoverPassword:
                str = cacheStore.GetString("RecoverPasswordLabel");
                break;
            case LogEventTypeEnum.ForgotPassword:
                str = cacheStore.GetString("ForgotPasswordLabel");
                break;
            case LogEventTypeEnum.FormAddition:
                str = cacheStore.GetString("FormAdditionLabel");
                break;
            case LogEventTypeEnum.FormDeletion:
                str = cacheStore.GetString("FormDeletionLabel");
                break;
            case LogEventTypeEnum.FormModification:
                str = cacheStore.GetString("FormModificationLabel");
                break;
            case LogEventTypeEnum.ElectronicResultFiling:
                str = cacheStore.GetString("ElectronicResultFilingLabel");
                break;
            case LogEventTypeEnum.ResumeSession:
                str = cacheStore.GetString("ResumeSession");
                break;
            case LogEventTypeEnum.PaymentRequestGeneration:
                str = cacheStore.GetString("PaymentRequestGenerationLabel");
                break;
            case LogEventTypeEnum.PaymentRequestDeletion:
                str = cacheStore.GetString("PaymentRequestDeletionLabel");
                break;
            case LogEventTypeEnum.PdfGenerated_MedicalNote:
            case LogEventTypeEnum.PdfGenerated_MedicationList:
            case LogEventTypeEnum.PdfGenerated_MedicalSummary:
            case LogEventTypeEnum.PdfGenerated_LabRequest:
            case LogEventTypeEnum.PdfGenerated_ConsultationRequest:
            case LogEventTypeEnum.PdfGenerated_GrowthCurve:
            case LogEventTypeEnum.PdfGenerated_VitalSigns:
            case LogEventTypeEnum.PdfGenerated_BillingReport:
            case LogEventTypeEnum.PdfGenerated_BillingReportCSV:
            case LogEventTypeEnum.PdfGenerated_PrivateBill:
            case LogEventTypeEnum.PdfGenerated_Prescription:
            case LogEventTypeEnum.PdfGenerated_ObstetricalFile:
                str = cacheStore.GetString("InformationsPrintExtractLabel") + " ( " + AdministrationHelper.getForcedDescription(eventType) + ")";
                break;
            case LogEventTypeEnum.XComPatientImport:
                str = cacheStore.GetString("XComPatientFileImportLabel");
                break;
            case LogEventTypeEnum.XComDocumentImport:
                str = cacheStore.GetString("XComDocumentImportLabel");
                break;
            case LogEventTypeEnum.FormSent:
                str = cacheStore.GetString("FormSentLabel");
                break;
            case LogEventTypeEnum.FormSentFailed:
                str = cacheStore.GetString("FormSentFailedLabel");
                break;
            case LogEventTypeEnum.Cnesst_InformationPrintedOrExtracted:
                str = cacheStore.GetString("Cnesst_InformationPrintedOrExtractedLabel");
                break;
            case LogEventTypeEnum.UserCreation:
                str = cacheStore.GetString("UserCreationLabel");
                break;
            case LogEventTypeEnum.UserModification:
                str = cacheStore.GetString("UserModificationLabel");
                break;
            case LogEventTypeEnum.UserDeactivationActivation:
                str = cacheStore.GetString("UserDeactivationActivationLabel");
                break;
            case LogEventTypeEnum.PatientCallCommunicationHistory_Delete:
                str = cacheStore.GetString("CallCenter_Correspondence_Log_Delete");
                break;
            case LogEventTypeEnum.PatientCallCommunicationHistory_Update:
                str = cacheStore.GetString("CallCenter_Correspondence_Log_Update");
                break;
            case LogEventTypeEnum.PatientCallCommunicationHistory_Create:
                str = cacheStore.GetString("CallCenter_Correspondence_Log_Create");
                break;
            case LogEventTypeEnum.UserPasswordChange:
                str = cacheStore.GetString("UserPasswordChangeLabel");
                break;
            case LogEventTypeEnum.PatientMergeRequest:
                str = cacheStore.GetString("MergePatientsLabel");
                break;
            case LogEventTypeEnum.PharmacyOrderComplete:
                str = cacheStore.GetString("PharmacyOrders_OrderCompletedLogMessage");
                break;
            case LogEventTypeEnum.PharmacyPatientConsentedSharingMedicalSummary:
                str = cacheStore.GetString("PharmacyOrders_UserConsentShareDataLogType");
                break;
            case LogEventTypeEnum.OntarioCommonDataSetExport_RequestCreated:
                str = cacheStore.GetString("CommonDataSetExport_RequestLabel");
                break;
            case LogEventTypeEnum.OntarioCommonDataSetExport_RequestCompleted:
                str = cacheStore.GetString("CommonDataSetExport_DownloadLabel");
                break;
            case LogEventTypeEnum.OntarioCommonDataSetImport_RequestCreated:
                str = cacheStore.GetString("CommonDataSetImport_RequestLabel");
                break;
            case LogEventTypeEnum.OntarioCommonDataSetImport_RequestCompleted:
                str = cacheStore.GetString("CommonDataSetImport_RequestCompleteLabel");
                break;
            case LogEventTypeEnum.OntarioCommonDataSetImport_RequestFailed:
                str = cacheStore.GetString("CommonDataSetImport_RequestFailedLabel");
                break;
            default:
                str = LogEventTypeEnum[eventType];
                break;
        }

        return str;
    }

    public static getForcedDescription(eventType: LogEventTypeEnum, baseDescription: string = null): string {
        if (eventType == null)
            return null;

        switch (eventType) {
            case LogEventTypeEnum.PdfGenerated_MedicalNote:
            case LogEventTypeEnum.PdfGenerated_MedicationList:
            case LogEventTypeEnum.PdfGenerated_MedicalSummary:
            case LogEventTypeEnum.PdfGenerated_LabRequest:
            case LogEventTypeEnum.PdfGenerated_ConsultationRequest:
            case LogEventTypeEnum.PdfGenerated_GrowthCurve:
            case LogEventTypeEnum.PdfGenerated_VitalSigns:
            case LogEventTypeEnum.PdfGenerated_BillingReport:
            case LogEventTypeEnum.PdfGenerated_BillingReportCSV:
            case LogEventTypeEnum.PdfGenerated_PrivateBill:
            case LogEventTypeEnum.PdfGenerated_Prescription:
            case LogEventTypeEnum.PdfGenerated_ObstetricalFile:
                return LogEventTypeEnum[eventType].replaceAll("PdfGenerated_", "");
            case LogEventTypeEnum.PharmacyPatientConsentedSharingMedicalSummary:
                return cacheStore.GetString("PharmacyOrders_UserConsentShareDataLogDescription").replace("{PharmacyName}", baseDescription);
        }

        return null;
    }

    public static get mspServiceLocationCodes(): { [code: string]: { code: string, description: string, obsolete?: boolean } } {
        return {
            A: { code: "A", description: "Practitioner’s Office – In Community", obsolete: true },
            B: { code: "B", description: "Community Health Centre" },
            C: { code: "C", description: "Continuing Care Facility" },
            D: { code: "D", description: "Diagnostic Facility" },
            E: { code: "E", description: "Hospital Emergency Dept Or Diagnostic Treatment Centre" },
            F: { code: "F", description: "Private Medical/Surgical Facility" },
            G: { code: "G", description: "Hospital – Day Care (Surgery)" },
            H: { code: "H", description: "Hospital" },
            I: { code: "I", description: "Hospital – Inpatient" },
            J: { code: "J", description: "First Nations Primary Health Care Clinic" },
            K: { code: "K", description: "Hybrid Primary Care Practice (part-time longitudinal practice, part-time walk-in clinic)" },
            L: { code: "L", description: "Longitudinal Primary Care Practice (e.g. GP family practice or PCN clinic)" },
            M: { code: "M", description: "Mental Health Centre" },
            N: { code: "N", description: "Health Care Practitioner Office (non-physician)" },
            O: { code: "O", description: "Physicians Office" },
            P: { code: "P", description: "Hospital - Outpatient" },
            Q: { code: "Q", description: "Specialist Physician Office " },
            R: { code: "R", description: "Patient’s Residence" },
            S: { code: "S", description: "PHC Community Location" },
            T: { code: "T", description: "Practitioner’s Office – In Publicly Administered Facility" },
            U: { code: "U", description: "Urgent and Primary Care Centre" },
            V: { code: "V", description: "Virtual Care Clinic" },
            W: { code: "W", description: "Walk-In Clinic" },
            Z: { code: "Z", description: "None of the above (e.g. accident site or ambulance)" }
        };
    }

    public static getCliniciansAsKeyValuePairs(): KeyValuePair<Guid, string>[] {
        return cacheStore
            .GetActiveEmployees()
            .sortByProperty(employee => employee.LastName)
            .map(employee => {
                const person = Person.FromEmployee(employee);
                return person.Licence
                    ? new KeyValuePair(person.ContactId, person.Name)
                    : null;
            })
            .filter(pair => pair != null);
    }

    /**
     * Get the default taxes for the default address in a given collection.
     * @param addresses The addresses
     */
    public static getTaxesForDefaultAddress(addresses: Address[]): DefaultCanadaTaxes | null {
        return AdministrationHelper.getTaxesForAddress(Address.getDefaultAddress(addresses));
    }

    /**
     * Get the default taxes for the given address.
     * @param address The address
     */
    public static getTaxesForAddress(address: Address): DefaultCanadaTaxes | null {
        if (!address?.AvailableCountryRegionLookupIds)
            return null;

        const regionLookupCode = address
            .AvailableCountryRegionLookupIds
            .firstOrDefault(region => region.LookupID === address.StateLookupId)
            ?.LookupCode;

        return !IsNullOrWhiteSpace(regionLookupCode)
            ? AdministrationHelper.getTaxesByProvinceCode(regionLookupCode)
            : null;
    }

    /**
     * Get the default taxes for the given province lookup code.
     * @param provinceLookupCode The province lookup code.
     */
    public static getTaxesByProvinceCode(provinceLookupCode: string): DefaultCanadaTaxes | null {
        return cacheStore
            .defaultCanadaTaxes
            .firstOrDefault(taxRate => taxRate.Myle_LookupCode === provinceLookupCode);
    }

    public static getLanguageName = (language: Locale | string): string => {
        switch (language) {
            case Locale.en:
                return cacheStore.GetString("ClinicInfo_SiteInfoInPortalLanguageEnglishLabelID");
            case Locale.fr:
                return cacheStore.GetString("ClinicInfo_SiteInfoInPortalLanguageFrenchLabelID");
            case Locale.es:
                return cacheStore.GetString("ClinicInfo_SiteInfoInPortalLanguageSpanishLabelID");
            default:
                return "";
        }
    };

    public static getDayOfWeekLabel = (dayOfWeek: DayOfWeek): string => {
        const dayOfWeekName = EnumHelper.getName(DayOfWeek, dayOfWeek);
        return cacheStore.GetString(`DayOfWeek${dayOfWeekName}Label`);
    };

    public static getLocalizedTextSiteViewArray(localizedText: LocalizedTextDto): LocalizedTextSite[] {
        if (!localizedText) {
            return [];
        }

        const result = this.orderedLocales.map(localeKey => {
            const pos = Object.keys(localizedText).findIndex(j => j.toLowerCase() === localeKey);
            const item = Object.keys(localizedText)[pos];
            return item;
        });
        return result.map(key => {
            const locale = Locale[key.toLowerCase()];

            return {
                locale,
                languageName: AdministrationHelper.getLanguageName(locale),
                value: localizedText[key],
                propertyName: locale.toTitleCase()
            };
        });
    }

    public static getLocalizedTextSiteCreateArray(localizedText: LocalizedTextDto): LocalizedTextSite[] {
        return this.orderedLocales.map(value => {
            const locale = value as Locale;
            const localizedValue = localizedText?.[locale.toTitleCase()] ?? "";

            return {
                locale,
                languageName: AdministrationHelper.getLanguageName(locale),
                value: localizedValue,
                propertyName: locale.toTitleCase()
            };
        });
    }

    public static getSpecialtyDictionaryOrLookupId(siteSpecialtyId: SiteSpecialtyId): string {
        return IsNullOrUndefined(siteSpecialtyId.DictionaryId) ? siteSpecialtyId.LookupId : siteSpecialtyId.DictionaryId;
    }

    public static getSpecialityByCurrentLanguage(siteSpecialtyId: SiteSpecialtyId): string {
        const licenseOrganizationSpecialization = !IsNullOrWhiteSpace(siteSpecialtyId.DictionaryId) && cacheStore.LicenseOrganizationSpecializationsById[siteSpecialtyId.DictionaryId];

        if (licenseOrganizationSpecialization) {
            switch (sessionStore.locale) {
                case Locale.en:
                    return licenseOrganizationSpecialization.NameEn;
                case Locale.fr:
                    return licenseOrganizationSpecialization.NameFr;
                case Locale.es:
                    return licenseOrganizationSpecialization.NameEs;
            }
        }

        return cacheStore.GetLocalizedValueByLookupId(siteSpecialtyId.LookupId);
    }

    /**
     * Populates the employee-related fields in the LogItem object based on the employee contact ID.
     * @param {LogItem} itemModel - The LogItem object to populate.
     * @param {string} employeeContactId - The employee's contact ID.
     */
    public static populateEmployeeDetails(itemModel: LogItem, employeeContactId: string): void {
        const employee = cacheStore.GetEmployeeByContactIdOrDefault(employeeContactId);
        if (employee) {
            itemModel.Role = LicenceOrganisationRule.getTitleName(Employee.getIssuingOrgRule(employee));
            itemModel.FirstName = employee.FirstName;
            itemModel.LastName = employee.LastName;
            itemModel.Username = employee.UserName;
        }
    }

    /**
     * Gets the formatted description for a log item.
     * @param description - The original description.
     * @param eventType - The type of log event.
     * @returns The formatted description.
     */
    public static async getFormattedLogItemDescription(description: string, eventType: LogEventTypeEnum): Promise<string> {
        let formatedDescription = "";
        if (eventType === LogEventTypeEnum.PharmacyPatientConsentedSharingMedicalSummary) {
            formatedDescription = await this.getPharmacyDescription(description);
        }
        else {
            formatedDescription = description && description.length > 0 ? description : "--";
        }

        formatedDescription = this.getForcedDescription(eventType, description) || formatedDescription;

        return formatedDescription;
    }

    /**
     * Retrieves the pharmacy description based on the log item description. Caches the pharmacy name if not already cached.
     * @param {string} description - The log item description.
     * @returns {Promise<string>} - A promise that resolves to the pharmacy name.
     * */
    private static async getPharmacyDescription(description: string): Promise<string> {
        const pharmacyNames: { [id: string]: string; } = {};

        if (pharmacyNames[description]) {
            return pharmacyNames[description];
        }

        const pharmacyName = await ContactDirectoryClient.getInstance().getPharmacyNameByClientTag(description);
        pharmacyNames[description] = pharmacyName;
        return pharmacyName;
    }
}

export { AdministrationHelper };
